/**
 * One slide below `lg`, for both `../SlideImageText.tsx` (Ryerson) and `../SlideImageTextSTS.tsx`
 * (dormant Southern Tool Steel build): a full-width background image, then the `h2` title, the
 * description and the link button. The image box's height is set in JavaScript to 60% of its
 * width when the slide mounts.
 */
import { css } from "@emotion/react";
import { ButtonLink } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import React from "react";

/** Copy of `../SlideImageText.tsx` → `SlideImageTextContent`, documented there. */
export type SlideImageTextContent = {
	imageUrl: string;
	title: string;
	description: string;
	jumpToLink: string;
	jumpToText: string | string[];
};

const mobileImage = css`
	width: 100%;
	margin: 0px 0;
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center center;
	padding-top: 10px;
	padding-bottom: 10px;
`;

/** One mobile slide; see the file header. Takes the slide's fields as props. */
const SlideImageContentMobileSection: React.FC<SlideImageTextContent> = ({
	imageUrl,
	title,
	description,
	jumpToLink,
	jumpToText,
}) => {
	const imageRef: any = React.createRef();
	const [imageHeight, setImageHeight] = React.useState("0px");
	// Sets the image height to 60% of its width on mount, and registers a resize listener that is
	// never removed. `createRef` makes a new ref on every render, and React clears the old ref when
	// it attaches the new one. The listener holds the first render's ref, so once the first height
	// update has re-rendered the slide it finds no element, and later resizes change nothing.
	React.useEffect(() => {
		const windowResized = (e?: any) => {
			if (imageRef && imageRef.current && imageRef.current.clientWidth) {
				setImageHeight(imageRef.current.clientWidth * 0.6 + "px");
			}
		};
		if (window) {
			window.addEventListener("resize", windowResized);
			windowResized();
		}
	}, []);
	return (
		<>
			<div
				ref={imageRef}
				style={{ backgroundImage: "url(" + imageUrl + ")", height: imageHeight }}
				css={mobileImage}
			/>
			<Typography
				variant="h2"
				css={css`
					margin-top: 25px;
					margin-bottom: 17px;
				`}
				type="primary"
			>
				{title}
			</Typography>
			<Typography
				variant="div"
				css={css`
					margin-bottom: 30px;
				`}
				size="md"
				type="primary"
			>
				{description}
			</Typography>
			<div
				css={css`
					display: block;
					margin-bottom: 25px;
				`}
			>
				<ButtonLink
					to={jumpToLink ?? "#"}
					label={jumpToText}
					type="primary"
					orientation="left"
					linkType="internal"
				/>
			</div>
		</>
	);
};

export default SlideImageContentMobileSection;
